import polyglotI18nProvider from 'ra-i18n-polyglot';
import {
    Admin,
    CustomRoutes,
    Resource,
    localStorageStore,
    useStore,
    StoreContextProvider,
} from 'react-admin';
import { Route } from 'react-router';

import authProvider from './authProvider';
import categories from './categories';
import { Dashboard } from './dashboard';
import englishMessages from './i18n/en';
import { Layout, Login } from './layout';
import orders from './orders';
import products from './products';
import reviews from './reviews';
import Segments from './segments/Segments';
import visitors from './visitors';
import { themes, ThemeName } from './themes/themes';
import dataProvider from './dataProvider';
import administration from './administration';
import roles from './roles';
import configuration from './configuration';
import flows from './flows';
import { AdminProvider } from './utils/AdminContext';
import user from './user';
import contractor from './contractor';
import transaction from './transaction';
import transactionHistory from './transactionHistory';
import promotions from './promotions';

const i18nProvider = polyglotI18nProvider(
    locale => {
        if (locale === 'fr') {
            return import('./i18n/fr').then(messages => messages.default);
        }

        // Always fallback on english
        return englishMessages;
    },
    'en',
    [
        { locale: 'en', name: 'English' },
        { locale: 'fr', name: 'Français' },
    ]
);

const store = localStorageStore(undefined, 'ECommerce');

const App = () => {
    const [themeName] = useStore<ThemeName>('themeName', 'soft');
    const lightTheme = themes.find(theme => theme.name === themeName)?.light;
    const darkTheme = themes.find(theme => theme.name === themeName)?.dark;
    return (
        <Admin
            title="Posters Galore Admin"
            dataProvider={dataProvider}
            store={store}
            authProvider={authProvider}
            dashboard={Dashboard}
            loginPage={Login}
            layout={Layout}
            i18nProvider={i18nProvider}
            disableTelemetry
            lightTheme={lightTheme}
            darkTheme={darkTheme}
            defaultTheme="light"
            requireAuth
        >
            <CustomRoutes>
                <Route path="/segments" element={<Segments />} />
            </CustomRoutes>
            <Resource name="customers" {...visitors} />
            <Resource name="administration" {...administration} />
            <Resource name="roles" {...roles}/>
            <Resource name="orders" {...orders} />
            <Resource name="contractor" {...contractor}/>
            <Resource name="transactions" {...transaction}/>
            <Resource name="transaction-history" {...transactionHistory} />
            <Resource name="promotions" {...promotions} />
            <Resource name="users" {...user} />
            <Resource name="flows" {...flows} />
            <Resource name="configuration" {...configuration}/>
            <Resource name="products" {...products} />
            <Resource name="categories" {...categories} />
            <Resource name="reviews" {...reviews} />
        </Admin>
    );
};

const AppWrapper = () => (
    <StoreContextProvider value={store}>
        <AdminProvider>
            <App />
        </AdminProvider>
    </StoreContextProvider>
);

export default AppWrapper;
